import { type QueryStatus } from "@tanstack/react-query";

/** Which state the discovery page's main / header / footer regions render. */
export type DiscoveryView =
  | "error"
  | "loading"
  | "grid"
  | "no-match" // students exist, a clearable filter excludes them all
  | "no-status" // till has students, but none in the selected status
  | "empty"; // till has no students at all

type DiscoveryViewArgs = {
  /** The students query status — the page's primary gate. */
  status: QueryStatus;
  /** The till has at least one student assigned (across all statuses). */
  hasStudents: boolean;
  /** A clearable filter is active (group / initial / non-default search field). */
  hasActiveFilters: boolean;
  /** At least one student survives the active filters. */
  hasMatches: boolean;
};

/**
 * Single source of truth for the discovery page's view state, so the header,
 * main grid, and footer can't disagree about what to render. Precedence:
 * error → loading → grid → no-match → no-status → empty.
 */
export function getDiscoveryView({
  status,
  hasStudents,
  hasActiveFilters,
  hasMatches
}: DiscoveryViewArgs): DiscoveryView {
  if (status === "error") return "error";
  if (status === "pending") return "loading";
  if (hasMatches) return "grid";
  // Students exist but the active filters exclude them all — offer to clear.
  if (hasStudents && hasActiveFilters) return "no-match";
  // Students exist, just none in the selected status — Clear wouldn't help.
  if (hasStudents) return "no-status";
  // The till has no students at all.
  return "empty";
}
